import React, { useState, useEffect } from 'react';
import { FormGroup, Label, Input, Button } from 'reactstrap';
import styled from 'styled-components';

const FormBox = styled.div`
  border: 2px solid #17a2b8;
  border-radius: 8px;
  padding: 20px;
  background: linear-gradient(135deg, #e0f7fa, #ffffff);
  margin-bottom: 20px;

  h5 {
    color: #17a2b8;
    margin-bottom: 15px;
    font-weight: 600;
  }
`;

const HelpText = styled.div`
  background: #d1ecf1;
  border-left: 4px solid #17a2b8;
  padding: 10px;
  margin-bottom: 15px;
  border-radius: 4px;
  font-size: 13px;
  color: #0c5460;
`;

const RadioOption = styled.label<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${props => props.isSelected ? '#e0f7fa' : 'white'};
  border: ${props => props.isSelected ? '2px solid #17a2b8' : '2px solid #e0e0e0'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 10px;

  &:hover {
    border-color: #17a2b8;
    background: #e0f7fa;
  }

  input {
    width: 20px;
    height: 20px;
    cursor: pointer;
  }

  span {
    font-size: 15px;
    font-weight: ${props => props.isSelected ? '600' : '400'};
    color: ${props => props.isSelected ? '#0c5460' : '#333'};
  }
`;

const PassagePreview = styled.div`
  background: white;
  padding: 15px;
  border-radius: 4px;
  border-left: 4px solid #17a2b8;
  margin-bottom: 12px;
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.6;
  max-height: 200px;
  overflow-y: auto;
`;

interface TrueFalseFormProps {
  formData: any;
  onChange: (data: any) => void;
}

const TrueFalseForm: React.FC<TrueFalseFormProps> = ({ formData, onChange }) => {
  const parseContent = () => {
    try {
      const parsed = typeof formData.content === 'string' && formData.content
        ? JSON.parse(formData.content)
        : formData.content || {};

      const passage = parsed.passage || '';

      // Backward compatibility: legacy single statement shape
      if (parsed.statements && Array.isArray(parsed.statements)) {
        const safeStatements = parsed.statements
          .filter((s: any) => s)
          .map((s: any) => ({ text: s.text || s.statement || '', is_true: Boolean(s.is_true ?? s.correct_answer ?? true) }));
        return {
          passage,
          statements: safeStatements.length > 0 ? safeStatements : [{ text: '', is_true: true }],
        };
      }

      const legacyStatement = parsed.statement || parsed.text || '';
      const legacyAnswer = parsed.correct_answer ?? parsed.is_true ?? true;
      return {
        passage,
        statements: [{ text: legacyStatement, is_true: Boolean(legacyAnswer) }],
      };
    } catch {
      return {
        passage: '',
        statements: [{ text: '', is_true: true }],
      };
    }
  };

  const [contentData, setContentData] = useState<{ passage: string; statements: { text: string; is_true: boolean }[] }>(parseContent());

  useEffect(() => {
    onChange({
      ...formData,
      content: JSON.stringify(contentData),
    });
  }, [contentData]);

  const handleFieldChange = (field: string, value: any) => {
    setContentData({
      ...contentData,
      [field]: value,
    });
  };

  const handleStatementChange = (idx: number, payload: Partial<{ text: string; is_true: boolean }>) => {
    const next = [...contentData.statements];
    next[idx] = { ...next[idx], ...payload };
    setContentData({ ...contentData, statements: next });
  };

  const handleAddStatement = () => {
    setContentData({
      ...contentData,
      statements: [...contentData.statements, { text: '', is_true: true }],
    });
  };

  const handleRemoveStatement = (idx: number) => {
    if (contentData.statements.length === 1) return; // keep at least one
    const next = contentData.statements.filter((_, i) => i !== idx);
    setContentData({ ...contentData, statements: next });
  };

  return (
    <FormBox>
      <h5>✓✗ Nội dung câu hỏi - Đúng/Sai (True/False)</h5>
      
      <HelpText>
        💡 <strong>Hướng dẫn:</strong> Cho một đoạn văn và một câu phát biểu, học sinh xác định Đúng hoặc Sai.
        <br/>
        Ví dụ: Đoạn văn: "Tom is 12 years old" → Phát biểu: "Tom is a teenager" → Sai
      </HelpText>

      <FormGroup>
        <Label>Đoạn văn hoặc thông tin: *</Label>
        <Input
          type="textarea"
          value={contentData.passage || ''}
          onChange={(e) => handleFieldChange('passage', e.target.value)}
          placeholder="Nhập đoạn văn hoặc thông tin...

Ví dụ: Tom is 12 years old. He lives in Hanoi with his family. He loves playing football."
          rows={5}
        />
        <small className="form-text text-muted">
          Thông tin cơ sở để học sinh đọc và đối chiếu với câu phát biểu
        </small>
      </FormGroup>

      <FormGroup>
        <Label>Các câu phát biểu (có thể nhiều): *</Label>
        {contentData.statements.map((stmt, idx) => (
          <div key={idx} style={{ marginBottom: '12px', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f8fbfd' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <strong>Câu {idx + 1}</strong>
              {contentData.statements.length > 1 && (
                <Button size="sm" color="danger" outline onClick={() => handleRemoveStatement(idx)}>
                  Xóa
                </Button>
              )}
            </div>
            <Input
              type="textarea"
              value={stmt.text}
              onChange={(e) => handleStatementChange(idx, { text: e.target.value })}
              placeholder="Nhập câu phát biểu cần xác định đúng/sai"
              rows={2}
              style={{ marginBottom: 10 }}
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <RadioOption 
                isSelected={stmt.is_true === true}
                onClick={() => handleStatementChange(idx, { is_true: true })}
              >
                <input
                  type="radio"
                  name={`correct_answer_${idx}`}
                  checked={stmt.is_true === true}
                  onChange={() => handleStatementChange(idx, { is_true: true })}
                />
                <span>✓ Đúng (True)</span>
              </RadioOption>
              <RadioOption 
                isSelected={stmt.is_true === false}
                onClick={() => handleStatementChange(idx, { is_true: false })}
              >
                <input
                  type="radio"
                  name={`correct_answer_${idx}`}
                  checked={stmt.is_true === false}
                  onChange={() => handleStatementChange(idx, { is_true: false })}
                />
                <span>✗ Sai (False)</span>
              </RadioOption>
            </div>
          </div>
        ))}
        <Button color="info" outline size="sm" onClick={handleAddStatement}>
          + Thêm câu phát biểu
        </Button>
      </FormGroup>

      {/* Preview */}
      <div style={{ 
        background: '#e0f7fa', 
        padding: '15px', 
        borderRadius: '4px',
        marginTop: '15px'
      }}>
        <strong style={{ fontSize: '13px', color: '#00838f' }}>📋 Xem trước:</strong>
        {contentData.passage && (
          <>
            <div style={{ 
              marginTop: '10px',
              fontSize: '12px',
              fontWeight: 'bold',
              color: '#00838f',
              marginBottom: '6px'
            }}>
              Đọc đoạn văn:
            </div>
            <PassagePreview>
              {contentData.passage}
            </PassagePreview>
          </>
        )}
        {contentData.statements.map((stmt, idx) => (
          <div key={idx} style={{ 
            background: 'white',
            padding: '12px',
            borderRadius: '4px',
            marginBottom: '12px',
            borderLeft: '4px solid #ffc107'
          }}>
            <strong style={{ fontSize: '12px', color: '#666' }}>
              Câu phát biểu {idx + 1}:
            </strong>
            <div style={{ marginTop: '6px', fontSize: '14px' }}>
              {stmt.text || '(Chưa nhập)'}
            </div>
            <div style={{ 
              marginTop: '8px',
              background: stmt.is_true ? '#d4edda' : '#f8d7da',
              padding: '8px',
              borderRadius: '4px',
              border: stmt.is_true ? '1px solid #28a745' : '1px solid #dc3545',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <strong style={{ color: stmt.is_true ? '#155724' : '#721c24' }}>
                {stmt.is_true ? '✓ ĐÚNG' : '✗ SAI'}
              </strong>
            </div>
          </div>
        ))}
      </div>

      {/* Examples */}
      <div style={{ 
        background: '#fff3cd', 
        padding: '12px', 
        borderRadius: '4px',
        marginTop: '15px',
        fontSize: '13px',
        borderLeft: '4px solid #ffc107'
      }}>
        <strong style={{ color: '#856404' }}>📖 Ví dụ:</strong>
        <div style={{ marginTop: '8px', color: '#555' }}>
          <div style={{ marginBottom: '10px' }}>
            <strong>Đoạn văn:</strong> "The Eiffel Tower is located in Paris, France."
            <br/>
            <strong>Phát biểu:</strong> "The Eiffel Tower is in London."
            <br/>
            <strong>Đáp án:</strong> ✗ <span style={{ color: '#dc3545', fontWeight: 'bold' }}>SAI</span>
          </div>
          <div>
            <strong>Đoạn văn:</strong> "Water boils at 100 degrees Celsius."
            <br/>
            <strong>Phát biểu:</strong> "Water boils at 100°C."
            <br/>
            <strong>Đáp án:</strong> ✓ <span style={{ color: '#28a745', fontWeight: 'bold' }}>ĐÚNG</span>
          </div>
        </div>
      </div>

      {/* Debug JSON */}
      <details style={{ marginTop: '15px' }}>
        <summary style={{ cursor: 'pointer', color: '#666', fontSize: '14px' }}>
          🔍 Xem JSON (Debug)
        </summary>
        <pre style={{ 
          background: '#f5f5f5', 
          padding: '10px', 
          borderRadius: '4px',
          fontSize: '12px',
          maxHeight: '200px',
          overflow: 'auto',
          marginTop: '10px'
        }}>
          {JSON.stringify(contentData, null, 2)}
        </pre>
      </details>
    </FormBox>
  );
};

export default TrueFalseForm;
